// ticket-photos-for-valet — full-size, keyboard-operable photo viewer.
//
// Accessibility (project requirement — visores operables por teclado):
//   - role="dialog" + aria-modal="true", labeled by the current photo's alt.
//   - Escape closes; ArrowLeft/ArrowRight navigate (no-op past the ends —
//     the prev/next buttons are simply not rendered there, so there is
//     nothing to "wrap around" into).
//   - Focus moves to the Close button on open and returns to the element
//     that opened the lightbox on close (the thumbnail button in
//     TicketPhotosPanel) — the trigger ref is captured once on mount.
//   - Tab is trapped within the dialog's own buttons (Close / Prev / Next)
//     so focus never silently escapes to the page behind the overlay.
import { useTranslation } from '@jq4race/i18n';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useRef } from 'react';

export interface LightboxPhoto {
  id: string;
  url: string;
  alt: string;
}

export interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

export function PhotoLightbox({ photos, index, onIndexChange, onClose }: PhotoLightboxProps) {
  const { t } = useTranslation('pickup-queue');
  const containerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    triggerRef.current = document.activeElement;
    closeRef.current?.focus();
    return () => {
      if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus();
    };
    // Runs once on mount/unmount only — capturing/restoring focus must not
    // re-fire on every index change (navigating photos stays inside the dialog).
  }, []);

  const photo = photos[index];
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'ArrowLeft' && hasPrev) {
      onIndexChange(index - 1);
      return;
    }
    if (e.key === 'ArrowRight' && hasNext) {
      onIndexChange(index + 1);
      return;
    }
    if (e.key === 'Tab') {
      const focusable = containerRef.current?.querySelectorAll<HTMLElement>('button');
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function handleBackdropClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  if (!photo) return null;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      onKeyDown={handleKeyDown}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4"
    >
      <div className="flex items-center justify-between text-white">
        <span className="jq-caption">
          {t('photosPanel.lightbox.counter', { current: index + 1, total: photos.length })}
        </span>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t('photosPanel.lightbox.closeAria')}
          className="rounded-sm p-2 text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center">
        {hasPrev ? (
          <button
            type="button"
            onClick={() => onIndexChange(index - 1)}
            aria-label={t('photosPanel.lightbox.prevAria')}
            className="absolute left-0 rounded-sm p-2 text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <ChevronLeft className="size-6" aria-hidden />
          </button>
        ) : null}

        <img src={photo.url} alt={photo.alt} className="max-h-full max-w-full object-contain" />

        {hasNext ? (
          <button
            type="button"
            onClick={() => onIndexChange(index + 1)}
            aria-label={t('photosPanel.lightbox.nextAria')}
            className="absolute right-0 rounded-sm p-2 text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <ChevronRight className="size-6" aria-hidden />
          </button>
        ) : null}
      </div>
    </div>
  );
}
